/**
 * useApplicationDashboardCompletion — live "application completion" for the new
 * director-form flow, computed from the published Form_Version + the applicant's
 * ApplicationResponse.
 *
 * NOTE (application-completion-tracker): this hook is the pre-existing
 * dashboard KPI. It was previously exported from `useApplicationCompletion.ts`,
 * but that filename is now owned by the shared-SSOT contestant adapter (design
 * §C2). This module keeps the original page/phase-based dashboard metric under
 * a distinct name so the ApplicantDashboard KPI is unchanged; the exported pure
 * `computeApplicationCompletion` keeps its original name and behavior.
 *
 * The dashboard's legacy Profile-Completion KPI counts populated
 * ApplicantYearProfile fragments — meaningless for a director-form contestant
 * who has no AYP. This hook computes the equivalent metric from the SAME inputs
 * the renderer uses: the phase-gated, division- and audience-visible pages
 * (`availablePages` + `isVisibleForDivision` + `isInAudience`), counting a page
 * complete when every required visible field on it is answered.
 *
 * Because it uses `availablePages(pages, applicationStatus)`, the denominator
 * GROWS the moment the application is APPROVED and Phase_2 pages unlock — so the
 * KPI drops when Phase 2 becomes available and climbs back as those pages are
 * filled, exactly the dynamic behavior the dashboard needs.
 */

import { useEffect, useMemo, useState } from 'react';

import { applicationFormClient } from '@/lib/application-form/client';
import type { ApplicationResponse } from '@/lib/application-form/client';
import {
  availablePages,
  isFieldVisibleForDivision,
  isInAudience,
  isVisibleForDivision,
  type Division,
  type FormFieldDef,
  type FormVersionSnapshot,
  type SnapshotPage,
  type SubmissionStatus,
} from '@/components/application-form/renderer/pure';

/** The audience the applicant renderer projects for (mirrors FormRenderer). */
const APPLICANT_AUDIENCE = 'APPLICANT' as const;

export interface ApplicationCompletion {
  /** Available visible pages whose required fields are all answered. */
  completed: number;
  /** Total available visible pages (Phase 1 + Phase 2 once APPROVED). */
  total: number;
  /** `completed / total` as a rounded percent. */
  percent: number;
}

/** Whether a stored response value carries a non-empty answer. */
function hasAnswer(value: unknown): boolean {
  if (value == null) return false;
  // Response values are stored as `{ kind, value }`; unwrap when present.
  const inner =
    typeof value === 'object' && value !== null && 'value' in (value as Record<string, unknown>)
      ? (value as { value: unknown }).value
      : value;
  if (inner == null) return false;
  if (typeof inner === 'string') return inner.trim() !== '';
  if (Array.isArray(inner)) return inner.length > 0;
  if (typeof inner === 'boolean') return inner === true;
  return true;
}

/**
 * Whether a field is one the APPLICANT actively fills — i.e. it counts toward
 * "did they complete this section". Custom fields and the Division / Title bound
 * controls qualify; Season is auto-derived, Contact/Identity comes from the
 * Person, and Document slots are gated by the uploader — none of those represent
 * work the applicant does on a page, so a page made up only of them is not a
 * "section to complete" and is excluded from the metric.
 */
function isEngageableField(field: FormFieldDef): boolean {
  if (field.fieldCategory !== 'BOUND') return true;
  return field.bindingTarget === 'DIVISION' || field.bindingTarget === 'TITLE_PREFERENCES';
}

/** Whether a single engageable field is satisfied. */
function fieldAnswered(
  field: FormFieldDef,
  values: Record<string, unknown>,
  ctx: { divisionKey: string; titlePreferences?: readonly string[] | null; approvedTitle?: string | null },
): boolean {
  if (field.fieldCategory === 'BOUND') {
    switch (field.bindingTarget) {
      case 'DIVISION':
        return ctx.divisionKey.trim() !== '';
      case 'TITLE_PREFERENCES':
        return (ctx.titlePreferences?.length ?? 0) > 0 || Boolean(ctx.approvedTitle);
      default:
        return false;
    }
  }
  return hasAnswer(values[field.id]);
}

/**
 * Compute page-level completion over the phase-gated, division- and
 * audience-visible pages. Pure and total.
 */
export function computeApplicationCompletion(args: {
  snapshot: FormVersionSnapshot;
  values: Record<string, unknown>;
  divisionKey: string;
  activeDivisions: readonly Division[];
  applicationStatus: SubmissionStatus;
  titlePreferences?: readonly string[] | null;
  approvedTitle?: string | null;
}): ApplicationCompletion {
  const { snapshot, values, divisionKey, activeDivisions, applicationStatus } = args;
  // Only apply division filtering when we actually know the division AND the
  // active set — otherwise an "all divisions" page (null keys) would be hidden,
  // undercounting. Falling back to audience-only keeps the metric stable.
  const useDivision = activeDivisions.length > 0 && divisionKey.trim() !== '';

  const phasePages = availablePages(snapshot.pages, applicationStatus);
  const visiblePages = phasePages.filter((page: SnapshotPage) => {
    const divisionVisible = useDivision
      ? isVisibleForDivision(page.visibleDivisionKeys ?? null, divisionKey, activeDivisions)
      : true;
    return divisionVisible && isInAudience(page.audienceSet, APPLICANT_AUDIENCE);
  });

  const answerCtx = {
    divisionKey,
    titlePreferences: args.titlePreferences,
    approvedTitle: args.approvedTitle,
  };

  let total = 0;
  let completed = 0;
  for (const page of visiblePages) {
    // The applicant-fillable, visible fields on this page.
    const engageableFields = page.fields.filter((field) => {
      if (!isEngageableField(field)) return false;
      const divisionVisible = useDivision
        ? isFieldVisibleForDivision(
            { visibleDivisionKeys: page.visibleDivisionKeys ?? null },
            { visibleDivisionKeys: field.visibleDivisionKeys ?? null },
            divisionKey,
            activeDivisions,
          )
        : true;
      return divisionVisible && isInAudience(field.audienceSet, APPLICANT_AUDIENCE);
    });

    // A page with nothing for the applicant to fill (e.g. only contact/identity
    // or document slots) is not a "section to complete" — skip it entirely.
    if (engageableFields.length === 0) continue;

    total += 1;

    const requiredFields = engageableFields.filter((f) => f.required);
    const allRequiredAnswered = requiredFields.every((f) => fieldAnswered(f, values, answerCtx));
    // Require genuine engagement: a page counts complete only when at least one
    // of its fillable fields is actually answered AND every required one is. This
    // is what stops an untouched Phase_2 page (no answers, or only optional
    // fields) from counting as complete via a vacuous "all required" pass.
    const anyAnswered = engageableFields.some((f) => fieldAnswered(f, values, answerCtx));
    if (allRequiredAnswered && anyAnswered) completed += 1;
  }

  return {
    completed,
    total,
    percent: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
}

export interface UseApplicationDashboardCompletionArgs {
  competitionId: string;
  response: ApplicationResponse | null;
  activeDivisions: readonly Division[];
  /** Skip the fetch entirely (e.g. legacy-AYP flow / dev preview). */
  enabled: boolean;
}

export interface UseApplicationDashboardCompletionResult {
  completion: ApplicationCompletion | null;
  loading: boolean;
}

/**
 * Load the active Form_Version and compute the live application completion for
 * the applicant's response. Returns `null` completion until the form resolves
 * (or when disabled / no response), so callers can fall back.
 */
export function useApplicationDashboardCompletion({
  competitionId,
  response,
  activeDivisions,
  enabled,
}: UseApplicationDashboardCompletionArgs): UseApplicationDashboardCompletionResult {
  const [snapshot, setSnapshot] = useState<FormVersionSnapshot | null>(null);
  const [loading, setLoading] = useState(false);

  const divisionKey = response?.divisionKey ?? '';

  useEffect(() => {
    if (!enabled || !competitionId || !response) {
      setSnapshot(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    applicationFormClient
      .getActiveFormVersion(competitionId, divisionKey)
      .then((version) => {
        if (cancelled) return;
        setSnapshot(((version?.snapshot ?? null) as FormVersionSnapshot | null) ?? null);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setSnapshot(null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `response` identity changes on refetch; `divisionKey` is derived from it.
  }, [enabled, competitionId, divisionKey, response]);

  const completion = useMemo<ApplicationCompletion | null>(() => {
    if (!snapshot || !response) return null;
    return computeApplicationCompletion({
      snapshot,
      values: (response.values ?? {}) as Record<string, unknown>,
      divisionKey,
      activeDivisions,
      applicationStatus: response.submissionStatus as SubmissionStatus,
      titlePreferences: response.titlePreferences,
      approvedTitle: response.approvedTitle,
    });
  }, [snapshot, response, divisionKey, activeDivisions]);

  return { completion, loading };
}

export default useApplicationDashboardCompletion;
